// react
import copy from "copy-to-clipboard";
import React, { useEffect, useState } from "react";
// redux
import { useSelector } from "react-redux";

import { faCircleQuestion, faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
// dependencies
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { Spinner, TextDate, Tooltip } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Button from "../../../components/Button";
import Chip from "../../../components/Chip";
import Toggle from "../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageTitle from "../../../components/PageTitle";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import Text from "../../../components/Text";
import DisplaySftpInfoModal from "./DisplaySftpInfoModal";
import RemoveConfirmationModal from "./RemoveConfirmationModal";

import ErrorPage from "../../../pages/Error";

import {
	useCompanyFunction,
	useDisableSftpPgpKey,
	useGenerateSftpPgpKey,
	useRbacPrimaryRolePermissions,
	useSftpPgpKey,
} from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
// hooks
import useRequest from "../../../hooks/useRequest";

// selectors
import { getUserCompanyID, getUserCompanyName } from "../../../selectors/app";

// utils
import get from "../../../utils/GetNested";

// styles
import css from "./Sftp.module.scss";

// actions

/** The other buttons on this page are all 37px tall; match them rather than using a smaller size. */
const BUTTON_STYLE = { height: "37px" };

/**
 * Downloads the armored public key as a `.asc` file, the format HRIS vendors expect. Kept separate
 * from `utils/downloadCSV` because that helper prepends a BOM, which would corrupt PGP armor.
 */
const downloadPublicKeyAsc = (publicKey, companyName) => {
	const file = new Blob([publicKey], { type: "application/pgp-keys" });
	const downloadLink = document.createElement("a");
	downloadLink.download = `${companyName || "performyard"}-sftp-public-key.asc`;
	downloadLink.href = window.URL.createObjectURL(file);
	downloadLink.style.display = "none";

	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
	window.URL.revokeObjectURL(downloadLink.href);
};

/**
 * Copies the public key, confirming it in a tooltip for a moment afterwards. The tooltip is shown
 * only in response to the click, never on hover, and the button label stays "Copy" so it doesn't
 * change width mid-interaction.
 */
const CopyPgpKeyButton = ({ publicKey, disabled }) => {
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (!copied) {
			return undefined;
		}

		const timeout = setTimeout(() => setCopied(false), 2000);
		return () => clearTimeout(timeout);
	}, [copied]);

	// The clipboard gets the canonical armor, trailing newline included — unlike the trimmed display.
	const handleCopy = () => {
		copy(publicKey, { format: "text/plain" });
		setCopied(true);
	};

	return (
		// Controlled off `copied` alone: no hover label, just the confirmation after a click (PY-14003).
		<Tooltip content="Key copied" placement="top" open={copied} asChild>
			<div className={css.pgpKeyActionWrapper}>
				<Button
					color="gray"
					style={BUTTON_STYLE}
					testid="copyPgpKeyButton"
					disabled={disabled}
					onClick={handleCopy}
				>
					Copy
				</Button>
			</div>
		</Tooltip>
	);
};

const SftpSettings = () => {
	const [sftpIntegrations, setSftpIntegrations] = useState([]);
	const [passwordType, setPasswordType] = useState("password");
	const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
	const [selectedIntegration, setSelectedIntegration] = useState("");
	const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
	const [url, setUrl] = useState("");

	const companyID = useSelector((state) => getUserCompanyID(state));
	const company = useSelector((state) => getUserCompanyName(state));

	// Function and feature flag queries for SFTP integration
	const { companyFunction: sftpIntegrationFunctionState, isLoading: isLoadingCompanyFunction } =
		useCompanyFunction("integrationSftp");
	const sftpIntegrationFunctionEnabled = sftpIntegrationFunctionState === "enabled";
	const {
		value: ffSftpIntegrationEnabled,
		query: { isLoading: isLoadingFeatureFlag },
	} = useFeatureFlagQuery("ff-is-enabled-sftp-integration", false);
	const { value: ffSftpPgpEnabled } = useFeatureFlagQuery("ff-temp-is-sftp-pgp-encryption-enabled", false);

	// The PGP endpoints resolve the same company_settings permissions, so mirror them here rather than
	// rendering controls that would 403 (billing-admins can view settings but not edit them).
	const { data: companySettingsPermissions } = useRbacPrimaryRolePermissions("company_settings");
	const canViewPgpKey = companySettingsPermissions?.includes("company_settings:viewSettings") ?? false;
	const canEditPgpKey = companySettingsPermissions?.includes("company_settings:editSettings") ?? false;

	// PGP keys live on internal-api, so this section uses react-query hooks rather than useRequest.
	const {
		data: pgpKeyData,
		isLoading: isLoadingPgpKey,
		error: pgpKeyError,
	} = useSftpPgpKey(ffSftpPgpEnabled && canViewPgpKey);
	const generatePgpKey = useGenerateSftpPgpKey();
	const disablePgpKey = useDisableSftpPgpKey();
	const pgpKey = pgpKeyData?.pgpKey ?? null;
	const pgpPublicKey = pgpKey?.publicKey ?? null;

	const { data: sftpIntegrationsData, revalidate: getSftpIntegrations } = useRequest(
		"getSftpIntegrations",
		{},
		{ skipCache: true }
	);

	const {
		data: sftpPasswordData,
		isRequesting: requestingPassword,
		revalidate: revalidateSftpPassword,
	} = useRequest("getSftpPassword", {}, { skipCache: true });

	const { revalidate: setSftpPassword } = useRequest("setSftpPassword", {}, { manual: true });

	const { revalidate: editSftpIntegration } = useRequest("editSftpIntegration", {}, { manual: true });

	const { revalidate: disableSftpPassword } = useRequest("disableSftpPassword", {}, { manual: true });

	useEffect(() => {
		if (sftpIntegrationsData && sftpIntegrationsData.results) {
			setSftpIntegrations(sftpIntegrationsData.results);
		}
	}, [JSON.stringify(sftpIntegrationsData)]);

	const handleSetSftpPassword = async () => {
		await setSftpPassword();
		revalidateSftpPassword();
	};

	const handleDisableSftpPassword = async () => {
		await disableSftpPassword();
		revalidateSftpPassword();
	};

	const handlePasswordType = () => {
		if (passwordType === "text") {
			setPasswordType("password");
		} else {
			setPasswordType("text");
		}
	};

	const handleGeneratePgpKey = () => {
		generatePgpKey.mutate();
	};

	const handleDisablePgpKey = () => {
		disablePgpKey.mutate();
	};

	const generateSftpUrl = (integrationID) => `sftp.performyard.com/${companyID}/${integrationID}/`;

	const handleCloseRemoveModal = () => {
		setIsRemoveModalOpen(false);
		getSftpIntegrations();
	};

	const handleAddSftpIntegration = () => {
		window.location = "/administration/sftp/create/";
	};

	const handleEditIntegration = (ii) => {
		const integration = sftpIntegrations[ii];
		const integrationID = get(integration, ["_id"], null);

		if (integrationID) {
			window.location = `/administration/sftp/edit/${integrationID}/`;
		}
	};

	const handleIntegrationStatus = async (ii, status) => {
		// get the id of the integration being updated
		const integrationID = get(sftpIntegrations, [ii, "_id"], "");

		// determine the new status
		const newStatus = status ? "enabled" : "disabled";

		// eagerly update the state to reflect the change
		const newIntegrationState = [...sftpIntegrations];
		newIntegrationState[ii].status = newStatus;
		setSftpIntegrations(newIntegrationState);

		// make the Edit request and refetch all data
		await editSftpIntegration({
			filters: { payload: { status: newStatus }, integrationID },
		});

		await getSftpIntegrations();
	};

	const handleDeleteIntegration = (ii) => {
		setSelectedIntegration(sftpIntegrations[ii]);
		setIsRemoveModalOpen(true);
	};

	const handleShowDetails = (integrationID) => {
		const sftpUrl = generateSftpUrl(integrationID);
		setUrl(sftpUrl);
		setIsInfoModalOpen(true);
	};

	// Show a loading spinner while the feature flag is being fetched
	if (isLoadingCompanyFunction || isLoadingFeatureFlag) {
		return <ComponentLoader />;
	}

	// Only show the error page after we've confirmed the feature flag is disabled
	if (!sftpIntegrationFunctionEnabled || !ffSftpIntegrationEnabled) {
		return <ErrorPage code={403} />;
	}

	const viewPassword = (
		<div>
			<Text size="sub-heading" weight="semi-bold">
				Password
			</Text>
			<div className={css.passwordAndShow}>
				<div>
					<input
						autoComplete="password"
						type={passwordType}
						value={sftpPasswordData ? get(sftpPasswordData, ["results", "sftp", "password"], "") : ""}
						onChange={() => {}}
						className={css.input}
						data-testid="sftpPasswordInput"
						readOnly
					/>
				</div>
				{passwordType === "password" ? (
					<div className={css.visibilityIcon} onClick={handlePasswordType}>
						<FontAwesomeIcon data-testid={faEye?.iconName} icon={faEye} />
					</div>
				) : (
					<div className={css.visibilityIcon} onClick={handlePasswordType}>
						<FontAwesomeIcon data-testid={faEyeSlash?.iconName} icon={faEyeSlash} />
					</div>
				)}
				<Button color="blue" style={{ height: "37px", marginRight: "7px" }} onClick={handleSetSftpPassword}>
					Update Password
				</Button>
				<Button color="red" style={{ height: "37px" }} onClick={handleDisableSftpPassword}>
					Disable Password
				</Button>
			</div>
		</div>
	);

	const isPgpMutating = generatePgpKey.isPending || disablePgpKey.isPending;

	const viewPgpKey = (
		<div className={css.pgpKeySection}>
			<div className={css.pgpKeyHeader}>
				<div className={css.pgpKeyLabel}>
					<Text size="sub-heading" weight="semi-bold" className={css.pgpKeyTitle}>
						PGP Key
					</Text>
					<Tooltip content="Share this public key with the system that sends files to PerformYard, such as UKG, so it can encrypt them before sending. PerformYard keeps the matching private key and decrypts files when they arrive.">
						<span className={css.pgpKeyHelpIcon} data-testid="pgpKeyHelpIcon">
							<FontAwesomeIcon icon={faCircleQuestion} />
						</span>
					</Tooltip>
				</div>
				<div className={css.pgpKeyActions}>
					{/* Copy and Download are the everyday read actions, so they stay lower-contrast than
					    the two controls that change which key is active. */}
					<CopyPgpKeyButton publicKey={pgpPublicKey ?? ""} disabled={isPgpMutating} />
					<Button
						color="gray"
						style={BUTTON_STYLE}
						testid="downloadPgpKeyButton"
						disabled={isPgpMutating}
						onClick={() => downloadPublicKeyAsc(pgpPublicKey, company)}
					>
						Download .asc
					</Button>
					{canEditPgpKey ? (
						<>
							<Button
								color="blue"
								style={BUTTON_STYLE}
								disabled={isPgpMutating}
								onClick={handleGeneratePgpKey}
							>
								Update PGP Key
							</Button>
							<Button
								color="red"
								style={BUTTON_STYLE}
								disabled={isPgpMutating}
								onClick={handleDisablePgpKey}
							>
								Disable PGP Key
							</Button>
						</>
					) : null}
				</div>
			</div>
			{/* A textarea, not an input: a single-line input strips the armor's newlines, and PGP armor
			    without them is not a valid key. Not masked — this is the public half of the keypair. */}
			<textarea
				className={css.pgpKeyTextarea}
				value={pgpPublicKey?.trimEnd() ?? ""}
				onChange={() => {}}
				data-testid="pgpKeyInput"
				spellCheck={false}
				aria-label="PGP public key"
				rows={5}
				readOnly
			/>
			{pgpKey ? (
				<div className={css.pgpKeyMeta} data-testid="pgpKeyMeta">
					<Text size="caption" weight="regular" color="neutral-5">
						{`Version ${pgpKey.keyVersion} · Generated`}
					</Text>
					<TextDate
						date={new Date(pgpKey.createdAt)}
						hasTime={false}
						dateSize="medium"
						size="caption"
						weight="regular"
						color="neutral-5"
					/>
				</div>
			) : null}
		</div>
	);

	return (
		<AdminContainer>
			<div className={css.sftpContainer}>
				<PageTitle title={`SFTP Setup - ${company} - PerformYard`} />
				<AdminTitle>SFTP Setup</AdminTitle>

				<div className={css.sftpPasswordContainer}>
					{!requestingPassword ? (
						<div>
							{sftpPasswordData &&
							get(sftpPasswordData, ["results", "sftp", "password"], null) !== null ? (
								viewPassword
							) : (
								<Button color="blue" style={{ height: "37px" }} onClick={handleSetSftpPassword}>
									Generate SFTP Password
								</Button>
							)}
						</div>
					) : (
						<div className={css.spinner}>
							<Spinner size={32} />
						</div>
					)}
				</div>

				{ffSftpPgpEnabled && canViewPgpKey ? (
					<div className={css.pgpKeyContainer}>
						{!isLoadingPgpKey ? (
							<div>
								{/* An unknown key state must not fall through to "Generate": generating is also the
								    rotate call, so offering it here would let a click supersede a key the company's
								    source system is already encrypting with. */}
								{pgpKeyError ? (
									<Text size="paragraph-2" weight="regular" testID="pgpKeyError">
										We couldn&apos;t load your PGP key. Refresh the page to try again.
									</Text>
								) : pgpPublicKey !== null ? (
									viewPgpKey
								) : canEditPgpKey ? (
									<Button
										color="blue"
										style={BUTTON_STYLE}
										disabled={isPgpMutating}
										onClick={handleGeneratePgpKey}
									>
										Generate PGP Key
									</Button>
								) : null}
							</div>
						) : (
							<div className={css.spinner}>
								<Spinner size={32} />
							</div>
						)}
					</div>
				) : null}

				<Text size="sub-heading" weight="semi-bold">
					SFTP Integrations
				</Text>

				{sftpIntegrations && sftpIntegrations.length < 10 ? (
					<Button onClick={handleAddSftpIntegration} style={{ height: "37px" }} color="blue">
						Add SFTP Integration
					</Button>
				) : null}
				{sftpIntegrations ? (
					<div className={css.sfptIntegrationsTableContainer}>
						<div className={css.columnLabels}>
							<div className={css.column}>Integration</div>
							<div className={css.column}>Enabled</div>
							<div className={css.column} />
							<div className={css.column} />
						</div>

						<div className={css.sftpIntegrationsTable}>
							{sftpIntegrations.map((item, ii) => {
								const id = item._id;
								const isEnabled = item.status === "enabled";
								const isTestingMode = item.is_testing === true;

								return (
									<div className={css.sftpObject} key={id}>
										<div className={css.integrationInfo}>
											<div
												className={`${css.cell} ${css.label}`}
												onClick={() => handleShowDetails(item._id)}
											>
												{get(item, ["name"], "")}
												{isTestingMode ? (
													<Chip
														className={css.testingChip}
														label="Testing Mode"
														size="small"
														color="orange"
													/>
												) : null}
											</div>

											<div
												data-testid="integrationToggleWrapper"
												className={`${css.cell} ${css.state}`}
											>
												<Toggle
													isLoading={false}
													onChange={(value) => handleIntegrationStatus(ii, value)}
													width={60}
													value={isEnabled}
												/>
											</div>
											<div className={`${css.cell} ${css.controls}`}>
												<Button
													color="grey"
													className={css.editButton}
													onClick={() => handleEditIntegration(ii)}
												>
													{" "}
													Edit
												</Button>
											</div>
											<div className={`${css.cell} ${css.controls}`}>
												<Button
													testid={`deleteIntegrationButton` + id}
													style={{
														display: "inline-block",
														margin: "0 3px",
														width: "40px",
														minWidth: "0",
													}}
													color="red"
													onClick={() => handleDeleteIntegration(ii)}
												>
													<FontAwesomeIcon icon={["fas", "trash"]} />
												</Button>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				) : null}
				{isRemoveModalOpen ? (
					<RemoveConfirmationModal
						handleClose={handleCloseRemoveModal}
						integrationToDelete={selectedIntegration}
					/>
				) : null}
				{isInfoModalOpen ? (
					<DisplaySftpInfoModal handleClose={() => setIsInfoModalOpen(!isInfoModalOpen)} url={url} />
				) : null}
			</div>
		</AdminContainer>
	);
};

export default SftpSettings;
